<template lang="pug">
.FImageContent(:style="style")
  .FImageContent__images
    Swiper(
      v-if="withSlider"
      slides-per-view="auto"
      slide-to-clicked-slide
      :speed="1000"
      :space-between="16"
      :threshold="10"
      :modules="[Autoplay, Keyboard, Pagination]"
      :autoplay="autoplaySettings"
      :keyboard="{ enabled: true }"
      :pagination="{ clickable: true }"
      @slide-change="onSlideChange"
    )
      SwiperSlide.FImageContent__image(
        v-for="image in images"
        :key="image.src"
      )
        FImage(
          v-bind="image"
          fit="cover"
          height="100%"
        )
    FImage(
      v-else
      v-bind="images[0]"
      fit="cover"
    )
  .FImageContent__content
    // @slot Component content
    slot
</template>

<style lang="stylus">
.FImageContent
  margin 0 auto
  display grid
  grid-template-columns 1fr 1fr
  align-items center

  .swiper-pagination-bullet-active
    background white

  +media-down('sm')
    grid-template-columns 1fr

.FImageContent__images
  margin-left initial
  margin-right initial
  order var(--FImageContent--imageOrder)
  align-self var(--FImageContent--alignImage)

  .swiper
    display flex
    justify-content center
    border-radius rem(16)

    .swiper-slide
      height auto

.FImageContent__image
  &:after
    content ''
    display block
    padding-bottom 100%

  img
    position absolute
</style>

<script setup lang="ts">
import { Autoplay, Keyboard, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';

import type { Swiper as ISwiper } from 'swiper';
import type { FImageProps } from '@fifteen/design-system-vue';
import type CSS from 'csstype';

import 'swiper/css/pagination';

export interface FImageContentProps {
  /**
   * Images of the component
   */
  images: FImageProps[];
  /**
   * Reverse the layout order
   */
  reverse?: boolean;
  /**
   * Alignment of the image with the content
   */
  alignImage?: CSS.Properties['alignSelf'];
}

const props = withDefaults(defineProps<FImageContentProps>(), {
  reverse: false,
  alignImage: 'auto',
});

const withSlider = computed(() => props.images.length > 1);

const autoplaySettings = computed(() => ({
  delay: 4000,
  disableOnInteraction: false,
}));

const activeSlideIndex = ref(0);

function onSlideChange(slide: ISwiper): void {
  activeSlideIndex.value = slide.activeIndex;
}

const style = computed(
  (): Style => ({
    '--FImageContent--imageOrder': props.reverse ? '1' : '0',
    '--FImageContent--alignImage': props.alignImage,
  })
);
</script>
